import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { createRoomSettingsAPI } from '../api/roomSettings';

export interface SensorData {
  roomId: string;
  roomName: string;
  sensorId: string;
  timestamp: string;
  temperature: number;
  humidity: number;
  battery?: number;
  doorStatus: 'open' | 'closed';
  status: 'online' | 'offline';
}

export interface FrigoSmartRoomData {
  device_id: string;
  room: string;
  humidity: number;
  temperature: number;
  magnet: boolean;
  local_time: string;
  epoch: number;
}

export interface AIContext {
  currentUser: {
    name: string;
    role: string;
    tenantId: string;
  };
  systemData: {
    rooms: any[];
    clients: any[];
    reservations: any[];
    loans: any[];
    currentTemperatures: { [roomId: string]: SensorData };
    systemStatus: string;
  };
  conversationHistory: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
}

export interface AIResponse {
  content: string;
  data?: any;
  actions?: string[];
  suggestions?: string[];
}

class AIService {
  private apiKey: string;
  private baseURL = 'https://api.openai.com/v1';
  private frigoSmartAPI = 'https://api.frigosmart.com';

  constructor() {
    this.apiKey = import.meta.env.VITE_OPENAI_API_KEY || '';
    if (!this.apiKey) {
      console.warn('OpenAI API key not found. AI features will be limited.');
    }
  }

  /**
   * Get system context from Firebase and API data
   */
  async getSystemContext(user: any): Promise<AIContext> {
    try {
      const tenantId = user.tenantId || 'demo-tenant-id';
      const roomAPI = createRoomSettingsAPI(tenantId);
      
      // Fetch rooms with sensors
      const rooms = await roomAPI.getAllRooms();
      
      // Fetch current temperatures from API
      const currentTemperatures = await this.getAllCurrentTemperatures(tenantId);
      
      // Fetch recent clients (simplified query without orderBy to avoid index requirement)
      let clients: any[] = [];
      try {
        const clientsSnapshot = await getDocs(
          query(
            collection(db, 'clients'),
            where('tenantId', '==', tenantId),
            limit(10)
          )
        );
        clients = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.warn('Failed to fetch clients:', error);
        clients = [];
      }

      // Fetch active reservations (simplified query)
      let reservations: any[] = [];
      try {
        const reservationsSnapshot = await getDocs(
          query(
            collection(db, 'reservations'),
            where('tenantId', '==', tenantId),
            where('status', 'in', ['active', 'approved']),
            limit(5)
          )
        );
        reservations = reservationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.warn('Failed to fetch reservations:', error);
        reservations = [];
      }

      // Fetch recent loans (simplified query)
      let loans: any[] = [];
      try {
        const loansSnapshot = await getDocs(
          query(
            collection(db, 'loans'),
            where('tenantId', '==', tenantId),
            limit(10)
          )
        );
        loans = loansSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.warn('Failed to fetch loans:', error);
        loans = [];
      }

      return {
        currentUser: {
          name: user.name || 'Utilisateur',
          role: user.role || 'user',
          tenantId
        },
        systemData: {
          rooms,
          clients,
          reservations,
          loans,
          currentTemperatures, // Populated from API
          systemStatus: 'operational'
        },
        conversationHistory: []
      };
    } catch (error) {
      console.error('Error getting system context:', error);
      throw new Error('Failed to load system context');
    }
  }

  /**
   * Get current temperature for a specific room
   */
  async getRoomTemperature(roomId: string, tenantId: string = 'demo-tenant-id'): Promise<SensorData | null> {
    try {
      // Get all temperatures from FrigoSmart API and find the specific room
      const allTemps = await this.getAllCurrentTemperatures(tenantId);
      
      // Try to find room by ID or name
      const roomData = allTemps[roomId] || 
        Object.values(allTemps).find(temp => 
          temp.roomName.toLowerCase().includes(roomId.toLowerCase()) ||
          temp.roomId === roomId
        );
      
      return roomData || null;
    } catch (error) {
      console.error('Error getting room temperature:', error);
      return null;
    }
  }

  /**
   * Get temperature history for a room (not available via FrigoSmart API)
   */
  async getTemperatureHistory(roomId: string, hours: number = 24, tenantId: string = 'demo-tenant-id'): Promise<any[]> {
    try {
      // FrigoSmart API doesn't provide historical data, so we return empty array
      // In a real implementation, you would need to store historical data in your own database
      console.warn(`Temperature history not available via FrigoSmart API for room ${roomId}`);
      return [];
    } catch (error) {
      console.error('Error getting temperature history:', error);
      return [];
    }
  }

  /**
   * Get all current sensor data for all rooms from FrigoSmart API
   */
  async getAllCurrentTemperatures(tenantId: string = 'demo-tenant-id'): Promise<{ [roomId: string]: SensorData }> {
    try {
      const response = await fetch(`${this.frigoSmartAPI}/rooms/latest`);
      
      if (!response.ok) {
        console.warn('FrigoSmart API not available, using fallback data');
        return this.getFallbackTemperatures(tenantId);
      }
      
      const result = await response.json();
      
      if (!result.data || !Array.isArray(result.data)) {
        console.warn('Invalid FrigoSmart API response, using fallback data');
        return this.getFallbackTemperatures(tenantId);
      }
      
      // Convert FrigoSmart data to our SensorData format
      const temperatures: { [roomId: string]: SensorData } = {};
      
      result.data.forEach((room: FrigoSmartRoomData, index: number) => {
        const roomId = `room-${index + 1}`;
        temperatures[roomId] = {
          roomId,
          roomName: room.room,
          sensorId: room.device_id,
          timestamp: room.local_time,
          temperature: room.temperature,
          humidity: room.humidity,
          battery: 85, // Default battery level since FrigoSmart doesn't provide it
          doorStatus: room.magnet ? 'closed' : 'open',
          status: 'online'
        };
      });
      
      console.log(`✅ Loaded ${Object.keys(temperatures).length} rooms from FrigoSmart API`);
      return temperatures;
    } catch (error) {
      console.warn('Error getting FrigoSmart temperatures, using fallback:', error);
      return this.getFallbackTemperatures(tenantId);
    }
  }

  /**
   * Generate fallback temperature data when API is not available
   */
  private getFallbackTemperatures(tenantId: string): { [roomId: string]: SensorData } {
    // Generate mock data based on real FrigoSmart structure
    const mockRooms = [
      'Chambre 1', 'Chambre 2', 'Chambre 3', 'Chambre 4', 
      'Chambre 5', 'Chambre 6', 'Chambre 7', 'Chambre 8'
    ];
    const temperatures: { [roomId: string]: SensorData } = {};
    
    mockRooms.forEach((roomName, index) => {
      const roomId = `room-${index + 1}`;
      temperatures[roomId] = {
        roomId,
        roomName,
        sensorId: `6925665`,
        timestamp: new Date().toLocaleString(),
        temperature: Math.round((Math.random() * 8 + 2) * 100) / 100, // 2-10°C with 2 decimals
        humidity: Math.round((Math.random() * 20 + 60) * 10) / 10, // 60-80%
        battery: 85, // Default battery level
        doorStatus: Math.random() > 0.2 ? 'closed' : 'open', // Most doors closed
        status: 'online'
      };
    });
    
    console.log('⚠️ Using fallback temperature data (FrigoSmart API not available)');
    return temperatures;
  }

  /**
   * Process user query with OpenAI
   */
  async processQuery(
    userQuery: string,
    context: AIContext,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<AIResponse> {
    if (!this.apiKey) {
      return this.getFallbackResponse(userQuery, context);
    }

    try {
      const systemPrompt = this.buildSystemPrompt(context);
      const messages = [
        { role: 'system', content: systemPrompt },
        ...conversationHistory.slice(-10), // Last 10 messages for context
        { role: 'user', content: userQuery }
      ];

      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',    
          messages,
          max_tokens: 500,
          temperature: 0.7,
        }),
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content || 'Désolé, je n\'ai pas pu traiter votre demande.';

      return {
        content,
        suggestions: this.extractSuggestions(content)
      };
    } catch (error) {
      console.error('Error processing AI query:', error);
      return this.getFallbackResponse(userQuery, context);
    }
  }

  /**
   * Build system prompt with current context
   */
  private buildSystemPrompt(context: AIContext): string {
    const { currentUser, systemData } = context;
    const temps = Object.values(systemData.currentTemperatures);
    const avgTemp = temps.length > 0 ? (temps.reduce((sum, t) => sum + t.temperature, 0) / temps.length).toFixed(1) : 'N/A';
    const minTemp = temps.length > 0 ? Math.min(...temps.map(t => t.temperature)).toFixed(1) : 'N/A';
    const maxTemp = temps.length > 0 ? Math.max(...temps.map(t => t.temperature)).toFixed(1) : 'N/A';
    
    return `You are a helpful AI assistant for cold storage facility management. You understand both French and Darija (Moroccan Arabic dialect).

USER CONTEXT:
- Name: ${currentUser.name}
- Role: ${currentUser.role}

CURRENT FACILITY DATA:
- ${systemData.rooms.length} rooms configured
- ${systemData.clients.length} recent clients
- ${systemData.reservations.length} active reservations
- ${systemData.loans.length} recent loans
- Average temperature: ${avgTemp}°C (Min: ${minTemp}°C, Max: ${maxTemp}°C)

REAL-TIME TEMPERATURE DATA:
${temps.map(temp => 
  `- ${temp.roomName}: ${temp.temperature}°C, Humidity: ${temp.humidity}%, Battery: ${temp.battery || 85}%, Door: ${temp.doorStatus === 'open' ? 'Open' : 'Closed'}, Last update: ${temp.timestamp}`
).join('\n')}

DARIJA TRANSLATIONS:
- "bab ma7lol" = door open
- "bab msdod" = door closed  
- "ch7al" = how much/temperature
- "wach" = is/are
- "f" = in
- "chambre" = room

INSTRUCTIONS:
- Respond naturally in the same language as the user (French or Darija)
- When asked about doors, provide door status with temperature info
- When asked about temperatures, provide specific room data
- Be conversational and helpful
- Use the real-time data when relevant
- Be friendly and professional`;
  }


  /**
   * Get room temperature response (let ChatGPT handle language naturally)
   */
  private getLocalizedRoomResponse(roomNum: string, temp: SensorData, language: string, userName: string): string {
    const doorStatus = temp.doorStatus === 'open' ? 'open' : 'closed';
    return `🌡️ Room ${roomNum} temperature: ${temp.temperature}°C\n💧 Humidity: ${temp.humidity}%\n🔋 Battery: ${temp.battery || 85}%\n🚪 Door: ${doorStatus === 'open' ? 'Open ⚠️' : 'Closed ✅'}\n⏰ Last update: ${temp.timestamp}`;
  }

  /**
   * Get room not found response (let ChatGPT handle language naturally)
   */
  private getLocalizedRoomNotFoundResponse(roomNum: string, availableRooms: string, language: string, userName: string): string {
    return `❌ Room ${roomNum} not found in current data, ${userName}.\n\nAvailable rooms: ${availableRooms}\n\nData loaded from ${Object.keys(this.getAllCurrentTemperatures()).length} rooms.`;
  }

 
  /**
   * Fallback response when OpenAI is not available
   */
  private getFallbackResponse(userQuery: string, context: AIContext): AIResponse {
    const query = userQuery.toLowerCase();
    const userName = context.currentUser.name;
    
    // Check for door status queries in Darija and French
    const doorMatch = query.match(/(?:bab|porte|door).*(?:ma7lol|msdod|ouvert|fermé|open|closed).*chambre\s*(\d+)|chambre\s*(\d+).*(?:bab|porte|door).*(?:ma7lol|msdod|ouvert|fermé|open|closed)/i);
    if (doorMatch) {
      const roomNum = doorMatch[1] || doorMatch[2];
      
      // Try to find temperature data directly from FrigoSmart data
      const temp = Object.values(context.systemData.currentTemperatures).find(t => 
        t.roomName === `Chambre ${roomNum}` ||
        t.roomName === `CH${roomNum}` ||
        t.roomName === roomNum ||
        t.roomName.toLowerCase().includes(`chambre ${roomNum}`) ||
        t.roomName.toLowerCase().includes(`ch${roomNum}`)
      );
      
      if (temp) {
        const doorStatus = temp.doorStatus === 'open' ? 'ouverte (ma7lol)' : 'fermée (msdod)';
        const doorIcon = temp.doorStatus === 'open' ? '🚪⚠️' : '🚪✅';
        const content = `${doorIcon} Chambre ${roomNum} - Porte ${doorStatus}\n🌡️ Température: ${temp.temperature}°C\n💧 Humidité: ${temp.humidity}%`;
        return {
          content,
          suggestions: ['Voir détails', 'Autres chambres']
        };
      } else {
        const availableRooms = Object.values(context.systemData.currentTemperatures).map(t => {
          const roomNum = t.roomName.match(/\d+/)?.[0] || t.roomName;
          return roomNum;
        }).join(', ');
        
        const content = `❌ Chambre ${roomNum} non trouvée.\n\nChambres disponibles: ${availableRooms}`;
        return {
          content,
          suggestions: ['Voir toutes les chambres', 'Actualiser']
        };
      }
    }
    
    // Check for specific room temperature queries
    const roomMatch = query.match(/temp.*chambre\s*(\d+)|chambre\s*(\d+).*temp|temp\s*f\s*chambre\s*(\d+)|temeprature.*chambre\s*(\d+)|ch7al.*chambre\s*(\d+)|chambre\s*(\d+).*ch7al/i);
    if (roomMatch) {
      const roomNum = roomMatch[1] || roomMatch[2] || roomMatch[3] || roomMatch[4];
      
      // Try to find temperature data directly from FrigoSmart data
      const temp = Object.values(context.systemData.currentTemperatures).find(t => 
        t.roomName === `Chambre ${roomNum}` ||
        t.roomName === `CH${roomNum}` ||
        t.roomName === roomNum ||
        t.roomName.toLowerCase().includes(`chambre ${roomNum}`) ||
        t.roomName.toLowerCase().includes(`ch${roomNum}`)
      );
      
      if (temp) {
        const content = this.getLocalizedRoomResponse(roomNum, temp, 'auto', userName);
        return {
          content,
          suggestions: ['View chart', 'History', 'Other rooms']
        };
      } else {
        // List available rooms
        const availableRooms = Object.values(context.systemData.currentTemperatures).map(t => {
          const roomNum = t.roomName.match(/\d+/)?.[0] || t.roomName;
          return roomNum;
        }).join(', ');
        
        const content = this.getLocalizedRoomNotFoundResponse(roomNum, availableRooms, 'auto', userName);
        return {
          content,
          suggestions: ['View all rooms', 'Refresh data']
        };
      }
    }
    
    if (query.includes('température') || query.includes('temp')) {
      const roomsWithSensors = context.systemData.rooms.filter(r => r.capteurInstalled);
      const temps = Object.values(context.systemData.currentTemperatures);
      
      if (temps.length > 0) {
        const avgTemp = (temps.reduce((sum, t) => sum + t.temperature, 0) / temps.length).toFixed(1);
        const minTemp = Math.min(...temps.map(t => t.temperature)).toFixed(1);
        const maxTemp = Math.max(...temps.map(t => t.temperature)).toFixed(1);
        
        let response = `📊 Températures actuelles:\n`;
        temps.forEach(temp => {
          response += `• ${temp.roomName}: ${temp.temperature}°C (${temp.humidity}% humidité)\n`;
        });
        response += `\n📈 Moyenne: ${avgTemp}°C | Min: ${minTemp}°C | Max: ${maxTemp}°C\n`;
        response += `\nVa dans "Capteurs" pour les graphiques détaillés.`;
        
        return {
          content: response,
          suggestions: ['Voir graphiques', 'Historique', 'Alertes']
        };
      }
      
      return {
        content: `Tu as ${context.systemData.rooms.length} chambres, ${roomsWithSensors.length} avec capteurs. Va dans "Capteurs" pour voir les données.`,
        suggestions: ['Voir capteurs', 'Installation capteurs']
      };
    }
    
    if (query.includes('client') || query.includes('réservation')) {
      return {
        content: `${context.systemData.clients.length} clients récents, ${context.systemData.reservations.length} réservations actives. Va dans "Clients" ou "Réservations".`,
        suggestions: ['Gérer clients', 'Voir réservations']
      };
    }
    
    if (query.includes('caisse') || query.includes('stock')) {
      return {
        content: `${context.systemData.loans.length} prêts récents. Va dans "Opérations" pour les prêts, "Réception" pour les entrées.`,
        suggestions: ['Voir opérations', 'Gérer stocks']
      };
    }
    
    return {
      content: 'Je peux t\'aider avec températures, clients, réservations, stocks. Qu\'est-ce que tu veux ?',
      suggestions: ['Températures', 'Clients', 'Stocks', 'Paiements']
    };
  }

  /**
   * Extract action suggestions from AI response
   */
  private extractSuggestions(content: string): string[] {
    const suggestions: string[] = [];
    
    if (content.includes('capteur') || content.includes('température')) {
      suggestions.push('Voir les capteurs');
    }
    if (content.includes('client') || content.includes('réservation')) {
      suggestions.push('Gérer les clients');
    }
    if (content.includes('caisse') || content.includes('stock')) {
      suggestions.push('Voir les opérations');
    }
    if (content.includes('paiement') || content.includes('facture')) {
      suggestions.push('Gérer les paiements');
    }
    
    return suggestions;
  }

  /**
   * Get specific room information
   */
  async getRoomInfo(roomId: string): Promise<any> {
    try {
      const roomAPI = createRoomSettingsAPI('default'); // You might want to pass tenantId
      return await roomAPI.getRoom(roomId);
    } catch (error) {
      console.error('Error getting room info:', error);
      return null;
    }
  }

  /**
   * Get client information
   */
  async getClientInfo(clientId: string): Promise<any> {
    try {
      const clientSnapshot = await getDocs(
        query(
          collection(db, 'clients'),
          where('__name__', '==', clientId)
        )
      );
      
      if (!clientSnapshot.empty) {
        const doc = clientSnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting client info:', error);
      return null;
    }
  }
}

// Singleton instance
export const aiService = new AIService();

// Export convenience functions
export const getAIContext = (user: any) => aiService.getSystemContext(user);
export const processAIQuery = (query: string, context: AIContext, history: any[]) => 
  aiService.processQuery(query, context, history);
export const getRoomTemperature = (roomId: string, tenantId?: string) => 
  aiService.getRoomTemperature(roomId, tenantId);
export const getTemperatureHistory = (roomId: string, hours?: number, tenantId?: string) => 
  aiService.getTemperatureHistory(roomId, hours, tenantId);
export const getAllCurrentTemperatures = (tenantId?: string) => 
  aiService.getAllCurrentTemperatures(tenantId);
